import { useState } from 'react'
import type { CorrectionDTO } from '@wf/shared'
import { correctionKindLabel } from '../corrections/labels'
import { formatDateDE } from '../time/format'
import { AddStampForm } from './AddStampForm'
import { useCorrectionsInbox, useResolveCorrection } from './useZeiten'

/** Aus der Antrags-Art die passende Stempelart fürs „Übernehmen"-Prefill ableiten. */
function kindForCorrection(kind: string | null): 'kommen' | 'gehen' | 'pause_start' {
  if (kind === 'gehen_vergessen') return 'gehen'
  if (kind === 'pause') return 'pause_start'
  return 'kommen'
}

/** Auto-Nachzügler-Antrag (R2.2b): der Server hat den Stempel STRUKTURIERT vorgeschlagen (proposed_*). Solche
 *  Anträge werden über „Genehmigen" abgeschlossen (Resolve materialisiert den Vorschlag GENAU EINMAL), NICHT über
 *  „Übernehmen" — das legt den Stempel manuell an und würde beim anschließenden Erledigen ein ZWEITES Mal
 *  materialisieren (Doppel-Stempel; adversarialer Review-Fund R3.3). */
function isAuto(c: CorrectionDTO): boolean {
  return !!(c.proposedKind && c.proposedAt)
}

/** Nur VERGESSENE MANUELLE Stempel lassen sich per reinem Nachtrag übernehmen. Bei „ändern"-Anträgen wäre der
 *  Nachtrag wirkungslos (der falsche Original-Stempel bliebe aktiv und das Pairing ignoriert den neuen) —
 *  dieser Weg führt über den Nachweis: erst entwerten, dann nachtragen (Review-Fund W9). Auto-Anträge sind
 *  NICHT adoptierbar (sie kommen über „Genehmigen", siehe isAuto). */
function isAdoptable(c: CorrectionDTO): boolean {
  return !isAuto(c) && (c.kind === 'kommen_vergessen' || c.kind === 'gehen_vergessen' || c.kind === 'pause') && !!c.corrTime
}

/**
 * Korrektur-Posteingang des Chefs (W9.3): offene Anträge zuerst (Server sortiert). Zwei Wege:
 * - MANUELLE Anträge: „Übernehmen" öffnet den Nachtrag vorbefüllt (Tag/Uhrzeit/Art) und schließt den Antrag nach
 *   dem Eintragen automatisch als erledigt — Original-Stempel bleiben unangetastet (append-only).
 * - AUTO-Nachzügler (R2.2b, proposed_* gesetzt): „Genehmigen" (Resolve/1.7) materialisiert den serverseitig
 *   vorgeschlagenen Stempel GENAU EINMAL. Kein „Übernehmen" — das würde ein zweites Mal stempeln (Doppel-Stempel).
 */
export function CorrectionsInbox() {
  const inbox = useCorrectionsInbox(true)
  const resolve = useResolveCorrection()
  const [adopting, setAdopting] = useState<CorrectionDTO | null>(null)

  if (inbox.isLoading) return <div className="muted">Anträge werden geladen…</div>
  if (inbox.isError)
    return (
      <div className="error" role="alert">
        Anträge konnten nicht geladen werden.
      </div>
    )
  const corrections = inbox.data?.corrections ?? []
  if (corrections.length === 0) {
    return (
      <div className="wf-empty">
        <div className="wf-empty__title">Keine Korrektur-Anträge</div>
        <div className="wf-empty__text">Anfragen deiner Mitarbeiter landen hier.</div>
      </div>
    )
  }

  return (
    <div className="wf-view wf-zstack">
      {adopting ? (
        // key: bei Antrag-zu-Antrag-Wechsel frisch mounten — sonst bleibt der Prefill des VORIGEN Antrags
        // im useState hängen und der Nachtrag landet mit falschen Werten (Review-Fund).
        <AddStampForm
          key={`adopt-${adopting.id}`}
          userId={adopting.userId}
          userName={adopting.userName}
          initialDay={adopting.corrDay ?? undefined}
          initialTime={adopting.corrTime ?? undefined}
          initialKind={kindForCorrection(adopting.kind)}
          onClose={() => setAdopting(null)}
          onSuccess={() => {
            resolve.mutate({ id: adopting.id, body: { status: 'erledigt' } })
            setAdopting(null)
          }}
        />
      ) : null}
      {resolve.isError ? (
        <p className="error" role="alert">
          Antrag konnte nicht abgeschlossen werden — bitte erneut versuchen (der Status steht noch auf „offen").
        </p>
      ) : null}
      <ul className="wf-linelist">
        {corrections.map((c) => {
          const open = c.status === 'offen'
          return (
            <li key={c.id} className="wf-line">
              <span className="wf-line__main">
                <span className="wf-line__name">
                  {c.userName} · {correctionKindLabel(c.kind)}
                  {c.corrDay ? ` · ${formatDateDE(c.corrDay)}` : ''}
                  {c.corrTime ? ` ${c.corrTime} Uhr` : ''}
                </span>
                {c.message ? <span className="wf-line__sub">{c.message}</span> : null}
              </span>
              {open ? (
                <>
                  {isAuto(c) ? (
                    // Auto-Nachzügler: „Genehmigen" materialisiert den serverseitig vorgeschlagenen Stempel EINMAL.
                    <button
                      type="button"
                      className="wf-btn wf-btn--primary wf-btn--sm"
                      onClick={() => resolve.mutate({ id: c.id, body: { status: 'erledigt' } })}
                      disabled={resolve.isPending}
                    >
                      Genehmigen
                    </button>
                  ) : isAdoptable(c) ? (
                    <button type="button" className="wf-btn wf-btn--sm" onClick={() => setAdopting(c)} disabled={resolve.isPending}>
                      Übernehmen
                    </button>
                  ) : c.kind === 'kommen_aendern' || c.kind === 'gehen_aendern' ? (
                    <span className="wf-tag" title="Erst den falschen Stempel im Nachweis entwerten, dann nachtragen — sonst zählt der alte weiter.">
                      → über Nachweis
                    </span>
                  ) : null}
                  {/* „Erledigt" (ohne Stempel) nur für MANUELLE Anträge — bei Auto-Anträgen ersetzt „Genehmigen" es. */}
                  {!isAuto(c) ? (
                    <button
                      type="button"
                      className="wf-btn wf-btn--ghost wf-btn--sm"
                      onClick={() => resolve.mutate({ id: c.id, body: { status: 'erledigt' } })}
                      disabled={resolve.isPending}
                    >
                      Erledigt
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="wf-btn wf-btn--ghost wf-btn--sm"
                    onClick={() => resolve.mutate({ id: c.id, body: { status: 'abgelehnt' } })}
                    disabled={resolve.isPending}
                  >
                    Ablehnen
                  </button>
                </>
              ) : (
                <span className={c.status === 'abgelehnt' ? 'wf-tag wf-tag--off' : 'wf-tag'}>{c.status}</span>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
